import { useCallback, useEffect, useRef, useState } from "react";
import { getTablePrefs, saveTablePrefs } from "../../services/uiPrefsApi";
import { emptyTablePrefs } from "./tablePrefs";

/**
 * Personal table layout of the signed-in user (Чат 4, правка №15). [Claude] 2026-11-02
 * Source of truth is the server (/v1/ui-preferences/<key>), so the layout follows the
 * account to any computer. The browser copy only avoids a "jump" while loading.
 * Changes are applied at once and saved with a short delay (column resize sends many).
 */
const SAVE_DELAY_MS = 600;
const cacheKey = (key) => `hr:table-prefs:${key}`;

function readCache(key) {
    try {
        const raw = window.localStorage.getItem(cacheKey(key));
        return raw ? { ...emptyTablePrefs(), ...JSON.parse(raw) } : null;
    } catch {
        return null;
    }
}

function writeCache(key, prefs) {
    try {
        window.localStorage.setItem(cacheKey(key), JSON.stringify(prefs));
    } catch {
        /* storage unavailable — the server copy is enough */
    }
}

export default function useTablePrefs(key) {
    const [prefs, setPrefs] = useState(() => readCache(key) || emptyTablePrefs());
    const [loaded, setLoaded] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const timer = useRef(null);
    const pending = useRef(null);

    useEffect(() => {
        let alive = true;
        getTablePrefs(key)
            .then((server) => {
                if (!alive || pending.current) return;
                const next = { ...emptyTablePrefs(), ...(server || {}) };
                setPrefs(next);
                writeCache(key, next);
            })
            .catch(() => { /* keep cached/default layout */ })
            .finally(() => { if (alive) setLoaded(true); });
        return () => { alive = false; };
    }, [key]);

    const flush = useCallback(async () => {
        const value = pending.current;
        if (!value) return;
        pending.current = null;
        try {
            await saveTablePrefs(key, value);
            setSaveError(null);
        } catch (error) {
            setSaveError(error?.message || String(error));
        }
    }, [key]);

    useEffect(() => () => {
        if (timer.current) clearTimeout(timer.current);
        void flush();
    }, [flush]);

    const update = useCallback((next) => {
        setPrefs(next);
        writeCache(key, next);
        pending.current = next;
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => { timer.current = null; void flush(); }, SAVE_DELAY_MS);
    }, [key, flush]);

    return { prefs, update, loaded, saveError };
}
